import type { ContentFragment } from '@oneaudi/falcon-tools';
import type {
  MenuItem,
  Search as LegacySearch,
  Link as LegacyLink,
  LoginLink,
  Suppliers,
} from '../interfaces/header-response.interfaces';
import type HeaderResponse from '../interfaces/header-response.interfaces';
import type { AudiHeaderConfig } from '../interfaces/header-components.interfaces';
import { matchCountryUrlOrigin } from './match-url-pattern';
import { relativeToAbsoluteURL } from './append-relative-url';
import { isAbsoluteUrl } from './is-absolute-url';
import { doHostsMatch } from './url-match';

export type FalconConfig = {
  // same identifier as for the content
  __type?: 'aem-headless';

  // Note: this is not required by default. It may only be required if you
  // override the options, so that the feature app config is required on your root CFM
  config?: Partial<ContentFragment> & {
    fields: AudiHeaderConfig;
  };
};

type Login = {
  fields: LoginLink;
};

type Search = {
  fields: LegacySearch;
};

type Link = {
  fields: {
    IsAudiSport: boolean;
    Text: string;
    Url: string;
    Type: string;
    AriaLabel: string;
    Target: string;
  };
};

export type CategoryType = 'basic' | `advancedCategoryBlocks${1 | 2 | 3 | 4}`;
// Mapped type for the 4 advanced blocks
type AdvancedCategoryBlocks = {
  advancedCategoryBlock1Label: string;
  advancedCategoryBlock1Reference: Link[];
  advancedCategoryBlock2Label: string;
  advancedCategoryBlock2Reference: Link[];
  advancedCategoryBlock3Label: string;
  advancedCategoryBlock3Reference: Link[];
  advancedCategoryBlock4Label: string;
  advancedCategoryBlock4Reference: Link[];
};

export type MainNavigationType = {
  fields: {
    Link: Link;
    CategoryType: CategoryType;
    SubNavigation: Link[];
  } & AdvancedCategoryBlocks;
};

type Fields = MainNavigationType['fields'];
export interface CategoryBlocks {
  Label: string;
  SubNavigation: LegacyLink[];
}

export type ContentHeadless = {
  __type: string;
  fields: {
    MenuLabel: string;
    LogoText: string;
    LogoUrl: string;
    Search?: Search;
    MainNavigation: MainNavigationType[];
    Login?: Login;
    SupplierName?: string;
    SupplierLinkLabel?: string;
    SupplierLinkUrl?: string;
  };
};

export function normalizeConfig(
  config?: AudiHeaderConfig | FalconConfig,
): AudiHeaderConfig | undefined {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (config && config?.__type === 'aem-headless') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore

    return config?.config?.fields as AudiHeaderConfig;
  }
  return config as AudiHeaderConfig;
}

const appendPartnerID = (url: string, partnerIdQueryString: string) => {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.append('partner_id', partnerIdQueryString);
    return urlObj.toString();
  } catch (error) {
    console.warn('Error appending partner ID to URL: ', error);
    return url;
  }
};

const appendPartnerIdCountryUrl = (
  urlToAppend: string,
  partnerIdQueryString: string,
  config?: AudiHeaderConfig,
) => {
  if (typeof window !== 'undefined') {
    const url = !isAbsoluteUrl(urlToAppend)
      ? `${window.location.protocol}//${urlToAppend}`
      : urlToAppend;

    if (matchCountryUrlOrigin(url, config?.tierOneUrl || '')) {
      return appendPartnerID(url, partnerIdQueryString);
    }
  }

  return urlToAppend;
};

const tierOneLogoUrl = (
  logoUrl: string,
  partnerIdQueryString: string,
  config?: AudiHeaderConfig,
): string => {
  let updatedLogoUrl = logoUrl;
  const isTierOneLogoUrl = isAbsoluteUrl(logoUrl)
    ? doHostsMatch(logoUrl, config?.tierOneUrl || '')
    : false;

  if (isTierOneLogoUrl) {
    if (partnerIdQueryString) {
      updatedLogoUrl = appendPartnerIdCountryUrl(updatedLogoUrl, partnerIdQueryString, config);
      return updatedLogoUrl;
    }
  }

  // Append partner ID if it exists already in the query string
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get('partner_id');
    if (param) {
      updatedLogoUrl = relativeToAbsoluteURL(logoUrl, config?.tierOneUrl || '');
      updatedLogoUrl = appendPartnerID(updatedLogoUrl, param);
      return updatedLogoUrl;
    }
  }

  return logoUrl;
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface NormalizeContentConfig
  extends Pick<AudiHeaderConfig, 'tierOneUrl' | 'OneShopEnabled'> {}

const createSubLinks = (fields: Link['fields'], url: string): LegacyLink => {
  return {
    Text: fields?.Text,
    Type: fields?.Type,
    Url: url,
    AriaLabel: fields?.AriaLabel,
    Target: fields?.Target,
    IsAudiSport: fields?.IsAudiSport,
  };
};

function buildSubNavigationLinks(
  subs: Link[],
  config?: NormalizeContentConfig,
  partnerIdQueryString?: string,
) {
  const result: LegacyLink[] = [];

  subs?.forEach((sub) => {
    let url = relativeToAbsoluteURL(sub.fields?.Url, config?.tierOneUrl || '');
    if (partnerIdQueryString) {
      url = appendPartnerIdCountryUrl(url, partnerIdQueryString, config);
    }
    result.push(createSubLinks(sub.fields, url));
  });

  return result;
}
export function normalizeContentHeadless(
  headless: ContentHeadless,
  config?: NormalizeContentConfig,
  partnerIdQueryString?: string,
): HeaderResponse {
  const MainNavigation: MenuItem[] = [];
  headless.fields?.MainNavigation.forEach((item) => {
    const SubNavigation: LegacyLink[] = [];
    const Blocks: CategoryBlocks[] = [];
    const blockCount = 4;

    for (let i = 1; i <= blockCount; i++) {
      const labelKey = `advancedCategoryBlock${i}Label` as keyof Fields;
      const refKey = `advancedCategoryBlock${i}Reference` as keyof Fields;

      const label = item.fields[labelKey] as string;
      const reference = item.fields[refKey] as Link[];
      const builtSubNavigationLinks = buildSubNavigationLinks(
        reference,
        config,
        partnerIdQueryString,
      );
      const categoryBlock = { Label: label, SubNavigation: builtSubNavigationLinks };

      Blocks.push(categoryBlock);
    }

    item.fields.SubNavigation?.forEach((sub) => {
      let url = relativeToAbsoluteURL(sub.fields?.Url, config?.tierOneUrl || '');
      if (partnerIdQueryString) {
        url = appendPartnerIdCountryUrl(url, partnerIdQueryString, config);
      }
      const subLink = createSubLinks(sub.fields, url);
      SubNavigation.push(subLink);
    });
    let mainUrl = relativeToAbsoluteURL(item?.fields?.Link?.fields?.Url, config?.tierOneUrl || '');
    if (partnerIdQueryString) {
      mainUrl = appendPartnerIdCountryUrl(mainUrl, partnerIdQueryString, config);
    }
    const menuLink = {
      Text: item?.fields?.Link?.fields?.Text,
      Type: item?.fields?.Link?.fields?.Type,
      Url: mainUrl,
      Target: item?.fields?.Link?.fields?.Target,
      AriaLabel: item?.fields?.Link?.fields?.AriaLabel,
      IsAudiSport: item?.fields?.Link?.fields?.IsAudiSport,
    };
    const menuItem: MenuItem = {
      Link: menuLink || undefined,
      CategoryType: item?.fields?.CategoryType,
      SubNavigation,
      Blocks,
    };
    MainNavigation.push(menuItem);
  });
  const suppliers: Suppliers[] = [
    {
      Name: headless.fields?.SupplierName || undefined,
      LinkUrl: headless.fields?.SupplierLinkUrl || undefined,
      LinkLabel: headless.fields?.SupplierLinkLabel || undefined,
    },
  ];
  return {
    MenuLabel: headless.fields.MenuLabel,
    Logo: {
      Text: headless.fields.LogoText,
      Url: tierOneLogoUrl(headless.fields.LogoUrl, partnerIdQueryString as string, config),
      Type: 'default',
      Target: '_self',
    },
    Login: headless.fields.Login?.fields || undefined,
    Search: headless.fields.Search?.fields || undefined,
    MainNavigation,
    OneShopEnabled: config?.OneShopEnabled || false,
    Suppliers: suppliers,
  };
}
